import { TASK_STATE } from "@core/constants"
import type { Task } from "@types"

/**
 * Avancement affiché d'une tâche asynchrone (DEV-1597).
 *
 * Règle : le pourcentage se déduit de ce que la tâche a publié, jamais d'un compteur
 * décoratif. Une tâche qui n'annonce aucune étape n'a pas de pourcentage du tout —
 * c'est `TaskToast` qui bascule alors la barre en mode indéterminé.
 *
 * Le palier de départ (25 %) matérialise « la tâche est partie » ; les 65 points suivants
 * sont la part réellement mesurée. On plafonne avant 100 % tant que la tâche n'a pas rendu
 * son résultat : une barre pleine devant un fichier absent est un mensonge.
 */

/** Palier « la tâche est prise en charge », avant tout travail mesurable. */
export const PROGRESS_STARTED = 25

/** Palier « la tâche est acceptée par Celery ». */
export const PROGRESS_RECEIVED = 10

/** Part de la barre réservée au travail réellement mesuré. */
const PROGRESS_MEASURED_SPAN = 65

/** Plafond tant que la tâche n'a pas rendu son résultat. */
const PROGRESS_MAX_RUNNING = 89

/** Incrément historique par sous-tâche, faute de mieux. */
const PROGRESS_GAP = 25

type ProgressTask = Task["progress_tasks"][number]

/** Dernière sous-tâche qui porte une vraie mesure (`step` sur `nsteps`). */
function lastMeasuredStep(task: Task): ProgressTask | undefined {
    const measured = (task.progress_tasks ?? []).filter(
        (pt) =>
            typeof pt.nsteps === "number" &&
            pt.nsteps > 0 &&
            typeof pt.step === "number"
    )
    return measured[measured.length - 1]
}

/**
 * Pourcentage d'avancement d'une tâche, ou `undefined` si rien ne permet de le calculer.
 *
 * Trois sources, de la plus fiable à la moins fiable :
 * 1. `subtasks_total` : sous-tâches Celery terminées sur total annoncé.
 * 2. `step` / `nsteps` : plan d'étapes publié par la tâche elle-même (exports asynchrones).
 * 3. Faute des deux, le comptage historique des sous-tâches vues.
 */
export function computeTaskProgressPercent(
    task: Task | undefined
): number | undefined {
    if (!task) return undefined

    switch (task.state) {
        case TASK_STATE.RECEIVED:
            return PROGRESS_RECEIVED
        case TASK_STATE.STARTED:
            return PROGRESS_STARTED
        case TASK_STATE.SUCCESS:
            return 100
        case TASK_STATE.PROGRESS:
            break
        default:
            return undefined
    }

    const completed = (task.progress_tasks ?? []).filter(
        (pt) => pt.state === TASK_STATE.SUCCESS
    ).length

    const total = task.subtasks_total
    if (typeof total === "number" && total > 0) {
        return Math.min(
            PROGRESS_MAX_RUNNING,
            Math.round(
                PROGRESS_STARTED + (completed / total) * PROGRESS_MEASURED_SPAN
            )
        )
    }

    // Plan d'étapes publié par la tâche : la seule mesure honnête d'un export asynchrone.
    const measured = lastMeasuredStep(task)
    if (measured) {
        const ratio = Math.min(1, (measured.step ?? 0) / (measured.nsteps ?? 1))
        return Math.min(
            PROGRESS_MAX_RUNNING,
            Math.round(PROGRESS_STARTED + ratio * PROGRESS_MEASURED_SPAN)
        )
    }

    const progressTasks = task.progress_tasks ?? []
    if (progressTasks.length === 0) return undefined

    // Comportement historique : chaque sous-tâche vue vaut un cran, sans mesure derrière.
    const minProgress = PROGRESS_STARTED + progressTasks.length * PROGRESS_GAP
    const last = progressTasks[progressTasks.length - 1]
    const progress =
        last?.state === TASK_STATE.SUCCESS ? minProgress + PROGRESS_GAP : minProgress
    return Math.min(90, progress)
}

/**
 * Avancement retenu pour l'affichage : le maximum entre ce qui était déjà montré et ce que
 * la tâche vient de publier. Une barre qui recule laisse croire à une reprise de zéro.
 */
export function keepHighestProgress(
    previous: number | undefined,
    next: number | undefined
): number | undefined {
    if (next === undefined) return previous
    if (previous === undefined) return next
    return Math.max(previous, next)
}
